import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import React from 'react'
import { MdOutlineSyncAlt } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { bodyMeasurementKeys } from '../../api/services/dd_panel_body_measurements/queryKeys.ts'
import { BodyMeasurement } from '../../api/services/dd_panel_body_measurements/types.ts'
import { patientProfilesApi } from '../../api/services/dd_panel_patient_profiles'
import Button from '../common/Button/Button.tsx'
import Card from '../common/Card/Card.tsx'
import HoverHint from '../common/HoverHint'
import BodyMeasurementsTable from './BodyMeasurementsTable.tsx'

const BodyMeasurements = ({
  patient_profile,
  patientFullName,
  patientPD3
}: {
  patient_profile: number
  patientFullName: string
  patientPD3: string | null
}) => {
  const queryClient = useQueryClient()

  const [disabled, setDisabled] = useState(false)
  const [bodyMeasurements, setBodyMeasurements] = useState<BodyMeasurement[] | null>(null)

  const navigate = useNavigate()
  const title = 'Pomiary'

  const syncMeasurements = async () => {
    setDisabled(true)
    const toastId = toast.loading('Rozpoczęto synchronizację danych...')
    const res =
      await patientProfilesApi.synchronizeBodyMeasurements(patient_profile)

    toast.update(toastId, {
      render:
        res.status === 200 ? 'Synchronizacja danych zakończona' : res.error,
      type: res.status === 200 ? 'success' : 'error',
      delay: 1500,
      isLoading: false,
      autoClose: 3000
    })

    const unsubscribe = toast.onChange((payload) => {
      if (payload.id === toastId && payload.status === 'removed') {
        setDisabled(false)
        unsubscribe()
      }
    })

    return res
  }

  const { mutateAsync } = useMutation({
    mutationFn: syncMeasurements,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: bodyMeasurementKeys.all,
        refetchType: 'active'
      })
    },
    onError: (error) => {
      console.error('Error synchronizing body measurements:', error)
    }
  })

  const onClicSync = async () => {
    await mutateAsync()
  }

  const onClickViewMore = () => {
    navigate(`/patient-profile/${patient_profile}/body-measurements/`, {
      state: {
        patientFullName
      }
    })
  }

  return (
    <React.Fragment>
      <Card.Title>
        <div className="h-10 flex flex-row items-center justify-between">
          <div>
            <span className="mr-3">{title}</span>

            {patientPD3 && (
              <Button
                variant="filled"
                size="small"
                color={'warning'}
                type="button"
                onClick={onClicSync}
                disabled={disabled}
                icon={<MdOutlineSyncAlt />}
              >
                <HoverHint
                  hintLabel={
                    'Pobierz nowe pomiary z PD3 i wyślij do aplikacji.'
                  }
                >
                  <span className={'text-white'}>Synchronizuj z PD3</span>
                </HoverHint>
              </Button>
            )}
          </div>

          <Button
            variant="filled"
            size="medium"
            color={'primary'}
            type="button"
            onClick={onClickViewMore}
          >
            {bodyMeasurements && bodyMeasurements.length > 0 ? 'Pokaż szczegółowe dane' : 'Dodaj pomiar'}
          </Button>
        </div>
      </Card.Title>

      <Card.Body>
        <BodyMeasurementsTable
          formAvailable={false}
          patient_profile={patient_profile}
          setBodyMeasurements={setBodyMeasurements}
        />
      </Card.Body>
    </React.Fragment>
  )
}

export default React.memo(BodyMeasurements)
